import { cn } from '@/lib/utils';
import { ArrowDown } from 'lucide-react';
import {
    MutableRefObject,
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react';

import { useChatMessages } from '@chainlit/react-client';

import { Button } from '@/components/ui/button';

interface Props {
    autoScrollUserMessage?: boolean;
    autoScrollRef?: MutableRefObject<boolean>;
    children: React.ReactNode;
    className?: string;
    threadId?: string; // Thread ID to detect thread switches and reset scroll state
}

export default function ScrollContainer({
    autoScrollRef,
    autoScrollUserMessage,
    children,
    className,
    threadId
}: Props) {
    const ref = useRef<HTMLDivElement>(null);
    const spacerRef = useRef<HTMLDivElement>(null);
    const lastUserMessageRef = useRef<HTMLDivElement | null>(null);
    const { messages } = useChatMessages();
    const [showScrollButton, setShowScrollButton] = useState(false);
    const [isScrolling, setIsScrolling] = useState(false);
    // Last observed scrollTop, used to tell a deliberate user scroll-UP (which
    // disengages follow) apart from the programmatic stick-to-bottom scrolls that
    // happen while a response streams in.
    const lastScrollTopRef = useRef(0);
    // Last observed scrollHeight. When it drops (a tool/thinking step removed when
    // the answer renders) the browser clamps scrollTop down — that is NOT a user
    // scroll-up, so we must not let it disengage follow.
    const lastScrollHeightRef = useRef(0);

    // Calculate and update spacer height
    const updateSpacerHeight = useCallback(() => {
        if (!ref.current) return;

        if (autoScrollUserMessage && lastUserMessageRef.current) {
            const containerHeight = ref.current.clientHeight;

            // Anchor on the fork divider when it sits immediately before the
            // newest user message, so the "private from here" boundary is visible
            // the moment the user writes their first message in a fork. It scrolls
            // away naturally on later turns (not sticky).
            const prevSibling = lastUserMessageRef.current
                .previousElementSibling as HTMLElement | null;
            const anchorEl =
                prevSibling?.getAttribute('data-fork-divider') === 'true'
                    ? prevSibling
                    : lastUserMessageRef.current;

            const anchorHeight = anchorEl.offsetHeight;

            // Calculate the height of all elements after the anchor
            let afterMessagesHeight = 0;
            let currentElement = anchorEl.nextElementSibling;

            // Iterate through all siblings after the anchor
            while (currentElement && currentElement !== spacerRef.current) {
                afterMessagesHeight += (currentElement as HTMLElement)
                    .offsetHeight;
                currentElement = currentElement.nextElementSibling;
            }

            // Position the anchor at the top with some padding
            // Subtract both the anchor height and the height of any elements after it
            const newSpacerHeight =
                containerHeight - anchorHeight - afterMessagesHeight - 32;

            // Only set a positive spacer height
            if (spacerRef.current) {
                spacerRef.current.style.height = `${Math.max(
                    0,
                    newSpacerHeight
                )}px`;
            }

            // Scroll to position the message at the top — but only while we're
            // still following. If the user has scrolled away (follow disengaged),
            // leave their scroll position alone instead of yanking the latest
            // question back to the top at the start of each answer.
            if (afterMessagesHeight === 0) {
                if (autoScrollRef?.current) scrollToPosition();
            } else if (autoScrollRef?.current) {
                ref.current.scrollTop = ref.current.scrollHeight;
            }
        } else if (autoScrollRef?.current) {
            ref.current.scrollTop = ref.current.scrollHeight;
        }
    }, [autoScrollUserMessage, autoScrollRef]);

    // Find and set a ref to the last user message element
    useEffect(() => {
        if (!ref.current) return;

        if (messages.length === 0 && spacerRef.current) {
            spacerRef.current.style.height = `0px`;
            return;
        }

        // Get all message elements
        const userMessages = ref.current.querySelectorAll(
            '[data-step-type="user_message"]'
        );
        if (userMessages.length > 0) {
            const lastUserMessage = userMessages[
                userMessages.length - 1
            ] as HTMLDivElement;
            lastUserMessageRef.current = lastUserMessage;

            // Update spacer height when last user message is found
            updateSpacerHeight();
        }
    }, [messages, updateSpacerHeight]);

    // Add window resize listener to update spacer height
    useEffect(() => {
        if (!autoScrollUserMessage) return;

        const handleResize = () => {
            updateSpacerHeight();
        };

        window.addEventListener('resize', handleResize);

        // Initial update
        updateSpacerHeight();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [autoScrollUserMessage, updateSpacerHeight]);

    // Keep the view pinned to the bottom (or the spacer in sync) as content
    // resizes — e.g. while a response streams in token by token. This runs in
    // BOTH layout modes so "follow mode" works regardless of the
    // user_message_autoscroll feature flag:
    //   • autoScrollUserMessage ON  → keep the last user message pinned to the top
    //     (updateSpacerHeight, which itself sticks to the bottom once the answer
    //      overflows and follow is engaged).
    //   • autoScrollUserMessage OFF → if follow is engaged (autoScrollRef.current,
    //     set by the scroll-to-bottom button or by the user being at the bottom),
    //     stick to the bottom as the streaming content grows. Scrolling up clears
    //     the flag (handleScroll) and disengages follow.
    useEffect(() => {
        if (!ref.current) return;

        const observer = new ResizeObserver(() => {
            if (!ref.current) return;
            if (autoScrollUserMessage) {
                if (lastUserMessageRef.current) {
                    updateSpacerHeight();
                }
            } else if (autoScrollRef?.current) {
                ref.current.scrollTop = ref.current.scrollHeight;
            }
        });

        // Observe all content children except the spacer
        const container = ref.current;
        for (let i = 0; i < container.children.length; i++) {
            const child = container.children[i];
            if (child !== spacerRef.current) {
                observer.observe(child);
            }
        }

        return () => {
            observer.disconnect();
        };
    }, [autoScrollUserMessage, autoScrollRef, updateSpacerHeight, messages]);

    // Check scroll position on mount
    useEffect(() => {
        if (!ref.current) return;

        setTimeout(() => {
            if (!ref.current) return;

            const { scrollTop, scrollHeight, clientHeight } = ref.current;
            const atBottom = scrollTop + clientHeight >= scrollHeight - 10;
            setShowScrollButton(!atBottom);
        }, 500);
    }, []);

    // Re-scroll to bottom when thread changes and messages are loaded
    const previousThreadId = useRef<string | undefined>();
    const hasScrolledForThread = useRef(false);

    // Reset scroll state when thread changes
    useEffect(() => {
        if (threadId !== previousThreadId.current) {
            hasScrolledForThread.current = false;
            previousThreadId.current = threadId;
        }
    }, [threadId]);

    // Scroll to bottom when messages load for a new thread
    useEffect(() => {
        // Need messages to be loaded
        if (messages.length === 0) return;

        // Only scroll once per thread
        if (hasScrolledForThread.current) return;
        if (!ref.current || !autoScrollRef?.current) return;

        hasScrolledForThread.current = true;

        // Use double requestAnimationFrame to ensure DOM has fully updated
        // First rAF schedules after current frame, second ensures layout is complete
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (ref.current) {
                    ref.current.scrollTop = ref.current.scrollHeight;
                }
            });
        });
    }, [messages, autoScrollRef]);

    const checkScrollEnd = () => {
        if (!ref.current) return;

        const prevScrollTop = ref.current.scrollTop;

        setTimeout(() => {
            if (!ref.current) return;

            const currentScrollTop = ref.current.scrollTop;
            if (currentScrollTop === prevScrollTop) {
                setIsScrolling(false);

                const { scrollTop, scrollHeight, clientHeight } = ref.current;
                const atBottom = scrollTop + clientHeight >= scrollHeight - 10;
                // Sync the baselines so the next user scroll is measured against
                // where this (smooth, programmatic) scroll actually settled.
                lastScrollTopRef.current = scrollTop;
                lastScrollHeightRef.current = scrollHeight;
                setShowScrollButton(!atBottom);
            } else {
                checkScrollEnd();
            }
        }, 100);
    };

    const scrollToBottom = () => {
        if (!ref.current) return;

        setIsScrolling(true);
        ref.current.scrollTo({
            top: ref.current.scrollHeight,
            behavior: 'smooth'
        });

        if (autoScrollRef) {
            autoScrollRef.current = true;
        }

        setShowScrollButton(false);
        checkScrollEnd();
    };

    const scrollToPosition = () => {
        if (!ref.current || !lastUserMessageRef.current) return;

        setIsScrolling(true);
        // Scroll to position the last user message at the top with some padding
        const scrollPosition = lastUserMessageRef.current.offsetTop - 20;

        ref.current.scrollTo({
            top: scrollPosition,
            behavior: 'smooth'
        });

        setShowScrollButton(false);
        checkScrollEnd();
    };

    const handleScroll = () => {
        if (!ref.current || isScrolling) return;
        const { scrollTop, scrollHeight, clientHeight } = ref.current;
        const atBottom = scrollTop + clientHeight >= scrollHeight - 10;

        // A genuine user scroll UP is the ONLY thing that disengages follow.
        // Two kinds of NON-user scrollTop drops must be ignored, or they would
        // race the stream and silently kill follow mid-answer (the bug where real
        // agent responses didn't autoscroll):
        //   1. The content list shrinking (a tool/thinking step removed when the
        //      answer renders) clamps scrollTop down — detect via scrollHeight drop.
        //   2. The programmatic stick-to-bottom scrolls only ever move DOWN.
        // With shrink handled explicitly, any remaining upward move is the user's,
        // so a tiny threshold (sub-pixel rounding only) is enough — letting even a
        // small deliberate scroll-up break away while a response streams.
        const contentShrank = scrollHeight < lastScrollHeightRef.current - 1;
        const scrolledUp =
            !contentShrank && scrollTop < lastScrollTopRef.current - 2;
        lastScrollTopRef.current = scrollTop;
        lastScrollHeightRef.current = scrollHeight;

        if (autoScrollRef) {
            if (scrolledUp && !atBottom) {
                autoScrollRef.current = false;
            } else if (atBottom) {
                autoScrollRef.current = true;
            }
        }

        // Hide the jump-to-bottom button whenever we're at the bottom OR still
        // actively following the stream; show it once the user has broken away.
        setShowScrollButton(!atBottom && !autoScrollRef?.current);
    };

    return (
        <div className="relative flex flex-col overflow-y-auto grow">
            <div
                ref={ref}
                className={cn(
                    'flex flex-col min-h-0 overflow-y-auto grow scrollbar-thin',
                    className
                )}
                onScroll={handleScroll}
            >
                {children}
                {/* Dynamic spacer to position the last user message at the top */}
                <div ref={spacerRef} className="shrink-0" />
            </div>

            {showScrollButton ? (
                <div className="right-0 bottom-4 left-0 absolute flex justify-center">
                    <Button
                        pill
                        size="icon"
                        variant="tertiary"
                        className="bg-background dark:bg-sidebar-accent"
                        onClick={scrollToBottom}
                    >
                        <ArrowDown />
                    </Button>
                </div>
            ) : null}
        </div>
    );
}
